import React, { useEffect, useRef, useState, useMemo } from "react";
import { motion } from "framer-motion";
import getTextColor from "@/utils/getTextColor";
import deriveShade from "@/utils/deriveShade";
import { SPINNER_VERBS } from "@/language.constants";

interface ReasoningDisplayProps {
  isVisible: boolean;
  reasoning: {
    text: string;
    ongoing: boolean;
  };
  chatConfig?: any;
  language?: string;
}

const MAX_HEADER_LEN = 60; // truncate length for dynamic headings
const FLIP_INTERVAL_MS = 10000;

function getSpinnerVerbs(language?: string): string[] {
  const languageKey = language?.toLowerCase();
  const spinnerVerbsByLanguage = SPINNER_VERBS as Record<string, string[]>;
  return (
    (languageKey && spinnerVerbsByLanguage[languageKey]) ||
    spinnerVerbsByLanguage.english || ["Thinking"]
  );
}

function getFallbackItems(spinnerVerbs: string[]): string[] {
  const firstVerb = spinnerVerbs[0] || "Thinking";
  const uniqueRestVerbs = Array.from(
    new Set(spinnerVerbs.slice(1).filter((verb) => verb !== firstVerb)),
  );

  for (let index = uniqueRestVerbs.length - 1; index > 0; index--) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [uniqueRestVerbs[index], uniqueRestVerbs[swapIndex]] = [
      uniqueRestVerbs[swapIndex],
      uniqueRestVerbs[index],
    ];
  }

  return [firstVerb, ...uniqueRestVerbs];
}

function cleanReasoningLine(line: string): string {
  return line
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[*_`~>#]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function extractReasoningDisplayItems(text: string): string[] {
  if (!text) return [];

  const items: string[] = [];
  let insideCodeBlock = false;

  text.split(/\r?\n/).forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) return;

    if (line.startsWith("```")) {
      insideCodeBlock = !insideCodeBlock;
      return;
    }

    if (insideCodeBlock) return;

    const headingMatch = line.match(/^#{1,6}\s+(.+)$/);
    const listMatch = line.match(/^(?:[-*+]\s+|\d+[.)]\s+)(.+)$/);
    const boldMatch = line.match(/\*\*([^\s*][^*]*?)\*\*/);

    if (!headingMatch && !listMatch && !boldMatch) return;

    const itemText = cleanReasoningLine(
      headingMatch?.[1] || listMatch?.[1] || line,
    );

    if (itemText && items[items.length - 1] !== itemText) {
      items.push(itemText);
    }
  });

  return items;
}

function truncateHeading(h: string): string {
  if (h.length <= MAX_HEADER_LEN) return h;
  return h.slice(0, MAX_HEADER_LEN - 1).trimEnd() + "…";
}

const ReasoningDisplay: React.FC<ReasoningDisplayProps> = ({
  reasoning,
  chatConfig,
  isVisible,
  language,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [displayText, setDisplayText] = useState("");
  const [displaySvgIndex, setDisplaySvgIndex] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const disableAutoToggle = useRef(false);
  const prevOngoingRef = useRef<boolean | null>(null);
  const prevDisplayItemsLengthRef = useRef(0);
  const shouldHide = !isVisible || (!reasoning.text && !reasoning.ongoing);

  const displayItems = useMemo(
    () => extractReasoningDisplayItems(reasoning.text),
    [reasoning.text],
  );
  const spinnerVerbs = useMemo(() => getSpinnerVerbs(language), [language]);
  const fallbackItems = useMemo(
    () => getFallbackItems(spinnerVerbs),
    [spinnerVerbs],
  );
  const flipItems = displayItems.length ? displayItems : fallbackItems;
  const isUsingFallback = !displayItems.length;

  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const activeItem =
    flipItems[activeItemIndex] || flipItems[flipItems.length - 1];
  const baseHeader = activeItem ? truncateHeading(activeItem) : spinnerVerbs[0];
  const headerText =
    isUsingFallback || reasoning.ongoing ? `${baseHeader}...` : baseHeader;
  const svgComponents = [BrainSvg, OpenSourceSvg, ReasonSvg];
  const CurrentSvgComponent = svgComponents[displaySvgIndex];

  useEffect(() => {
    if (isUsingFallback) {
      prevDisplayItemsLengthRef.current = 0;
      setActiveItemIndex(0);
      return;
    }

    const previousLength = prevDisplayItemsLengthRef.current;
    prevDisplayItemsLengthRef.current = displayItems.length;

    if (displayItems.length > previousLength) {
      setActiveItemIndex(displayItems.length - 1);
      return;
    }

    setActiveItemIndex((prevIndex) =>
      Math.min(prevIndex, displayItems.length - 1),
    );
  }, [displayItems.length, fallbackItems, isUsingFallback]);

  useEffect(() => {
    if (flipItems.length <= 1) return;

    const interval = window.setInterval(() => {
      setActiveItemIndex((prevIndex) => (prevIndex + 1) % flipItems.length);
    }, FLIP_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [flipItems.length]);

  useEffect(() => {
    if (displayText === "") {
      setDisplayText(headerText);
    }
  }, [headerText, displayText]);

  useEffect(() => {
    if (headerText !== displayText && displayText !== "") {
      setIsFlipping(true);

      const exitTimer = setTimeout(() => {
        setDisplayText(headerText);
        setDisplaySvgIndex(
          (prevIndex) => (prevIndex + 1) % svgComponents.length,
        );
        const enterTimer = setTimeout(() => setIsFlipping(false), 500);
        return () => clearTimeout(enterTimer);
      }, 150);

      return () => clearTimeout(exitTimer);
    }
  }, [headerText, displayText, svgComponents.length]);

  useEffect(() => {
    if (shouldHide) return;

    const prevOngoing = prevOngoingRef.current;

    if (
      reasoning.ongoing &&
      prevOngoing === false &&
      !disableAutoToggle.current
    ) {
      setIsExpanded(true);
    }

    if (
      prevOngoing &&
      !reasoning.ongoing &&
      reasoning.text &&
      !disableAutoToggle.current
    ) {
      setIsExpanded(false);
    }

    prevOngoingRef.current = reasoning.ongoing;
  }, [reasoning.ongoing, reasoning.text, shouldHide]);

  if (prevOngoingRef.current === null) {
    prevOngoingRef.current = reasoning.ongoing;
  }

  const dynamicColors = useMemo(() => {
    const baseShade = deriveShade(chatConfig?.chatBgColor);
    return {
      primary: `rgba(${baseShade}, 0.6)`,
      secondary: `rgba(${baseShade}, 0.3)`,
      shine: `rgba(${baseShade}, 0.9)`,
    };
  }, [chatConfig?.chatBgColor]);

  const handleToggle = () => {
    disableAutoToggle.current = true;
    setIsExpanded(!isExpanded);
  };

  if (shouldHide) {
    return null;
  }

  return (
    <div className="botleft !mb-0">
      <motion.div
        initial={{ opacity: 0, y: -5 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -5 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
      >
        {/* Header */}
        <div
          className="group flex items-baseline gap-1 pb-1"
          onClick={handleToggle}
        >
          <div className="thinking-text-container">
            <div className="flex items-center gap-[4px]">
              <div
                className={`svg-container ${isFlipping ? (headerText === displayText ? "text-flip-enter" : "text-flip-exit") : ""}`}
              >
                <CurrentSvgComponent
                  color={getTextColor(chatConfig?.chatBgColor)}
                />
              </div>
              <span
                className={`thinking-multi-breathe thinking-shine text-[12px] font-medium transition-opacity ${isFlipping ? (headerText === displayText ? "text-flip-enter" : "text-flip-exit") : ""}`}
                title={activeItem || spinnerVerbs[0]}
                style={
                  {
                    color: getTextColor(chatConfig?.chatBgColor),
                    transition: "filter 0.3s ease",
                    transformStyle: "preserve-3d",
                    position: "relative",
                    "--sweep-bg-primary": `linear-gradient(90deg, transparent 0%, ${dynamicColors.primary} 50%, transparent 100%)`,
                    "--sweep-bg-secondary": `linear-gradient(90deg, transparent 0%, ${dynamicColors.secondary} 50%, transparent 100%)`,
                    "--shine-color": dynamicColors.shine,
                  } as React.CSSProperties
                }
              >
                {displayText}
              </span>
              {/* <motion.span
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ duration: 0.15 }}
            className={`text-xs group-hover:opacity-70`}
            style={{ color: getTextColor(chatConfig?.botBubbleColor) }}
          >
            ▶
          </motion.span> */}
            </div>
            {/* Expandable Content */}
            {/* <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="mt-2 overflow-hidden rounded-[12px]"
            >
              <div
                className={`rounded-md p-3 text-xs leading-relaxed opacity-80`}
                style={{
                  background: chatConfig?.botBubbleColor,
                }}
              >
                {reasoning.text ? (
                  <Markdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code({ node, className, children, ...props }: any) {
                        const match = /language-(\w+)/.exec(className || "");
                        const isInline = !match;
                        return !isInline ? (
                          <SyntaxHighlighter
                            style={isDark ? themeDark : themeLight}
                            language={match[1]}
                            PreTag="div"
                            className="!bg-transparent text-xs"
                            customStyle={{
                              background: "transparent",
                              fontSize: "0.75rem",
                            }}
                          >
                            {String(children).replace(/\n$/, "")}
                          </SyntaxHighlighter>
                        ) : (
                          <code
                            className={`rounded px-1 py-0.5 font-mono text-xs`}
                            style={{ color: chatConfig?.typography?.color }}
                          >
                            {children}
                          </code>
                        );
                      },
                      p: ({ children }) => (
                        <p
                          className="mb-1.5 text-xs last:mb-0"
                          style={{ color: chatConfig?.typography?.color }}
                        >
                          {children}
                        </p>
                      ),
                      ul: ({ children }) => (
                        <ul
                          className="mb-1.5 list-inside list-disc space-y-0.5 text-xs"
                          style={{ color: chatConfig?.typography?.color }}
                        >
                          {children}
                        </ul>
                      ),
                      ol: ({ children }) => (
                        <ol
                          className="mb-1.5 list-inside list-decimal space-y-0.5 text-xs"
                          style={{ color: chatConfig?.typography?.color }}
                        >
                          {children}
                        </ol>
                      ),
                      blockquote: ({ children }) => (
                        <blockquote
                          className={`mb-1.5 border-l-2 pl-2 text-xs italic`}
                          style={{ color: chatConfig?.typography?.color }}
                        >
                          {children}
                        </blockquote>
                      ),
                    }}
                  >
                    {reasoning.text}
                  </Markdown>
                ) : (
                  <div className="flex items-center space-x-2 text-xs">
                    <span className="opacity-70">Processing your request</span>
                    <div className="flex space-x-0.5">
                      {[0, 1, 2].map((i) => (
                        <motion.div
                          key={i}
                          className="h-0.5 w-0.5 rounded-full bg-current opacity-60"
                          animate={{ opacity: [0.3, 1, 0.3] }}
                          transition={{
                            duration: 1.5,
                            repeat: Infinity,
                            delay: i * 0.2,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence> */}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ReasoningDisplay;

export const OpenSourceSvg = ({ color }) => (
  <svg
    className="brain-bounce"
    width={12}
    height={12}
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10.5 6C9.67185 6 9 6.67185 9 7.5C9 8.1519 9.41895 8.7012 10.0001 8.9082V9.3501L6.49995 10.9058V9.39945L8.25 8.6217V6.3057L9.49995 5.74995V4.6581C10.081 4.4514 10.5 3.9018 10.5 3.25005C10.5 2.4219 9.82815 1.75005 9 1.75005C8.17185 1.75005 7.5 2.4219 7.5 3.25005C7.5 3.90195 7.91895 4.45155 8.50005 4.65825V5.1L7.24995 5.65575V7.97205L6.49995 8.30565V2.9082C7.08105 2.70135 7.5 2.15205 7.5 1.5C7.5 0.6717 6.82815 0 6 0C5.1717 0 4.5 0.6717 4.5 1.5C4.5 2.15205 4.9188 2.70135 5.50005 2.9082V7.0653L3.5001 5.7324V5.1582C4.08105 4.95135 4.5 4.40205 4.5 3.75C4.5 2.9217 3.82815 2.25 3 2.25C2.17185 2.25 1.5 2.9217 1.5 3.75C1.5 4.40205 1.91895 4.95135 2.50005 5.1582V6.2676L5.50005 8.2671V12H6.5001L11.0001 10.0001V8.9082C11.581 8.7012 12 8.1519 12 7.5C12 6.67185 11.3282 6 10.5 6ZM9 2.74995C9.06567 2.74989 9.13071 2.76278 9.19139 2.78789C9.25208 2.81299 9.30721 2.84981 9.35365 2.89625C9.40009 2.94269 9.43691 2.99782 9.46201 3.05851C9.48712 3.11919 9.50001 3.18423 9.49995 3.2499C9.49995 3.52635 9.27645 3.75 9 3.75C8.72355 3.75 8.50005 3.52635 8.50005 3.25005C8.49999 3.18437 8.51288 3.11933 8.53798 3.05863C8.56309 2.99794 8.59991 2.9428 8.64634 2.89635C8.69278 2.8499 8.74791 2.81306 8.8086 2.78794C8.86928 2.76282 8.93432 2.74991 9 2.74995ZM3 4.24995C2.93363 4.2511 2.86769 4.23902 2.80603 4.21442C2.74438 4.18981 2.68824 4.15317 2.6409 4.10664C2.59355 4.06011 2.55595 4.00462 2.53028 3.9434C2.50461 3.88218 2.49139 3.81646 2.49139 3.75008C2.49139 3.68369 2.50461 3.61797 2.53028 3.55675C2.55595 3.49553 2.59355 3.44004 2.6409 3.39351C2.68824 3.34698 2.74438 3.31034 2.80603 3.28574C2.86769 3.26113 2.93363 3.24905 3 3.2502C3.13108 3.25247 3.25602 3.30613 3.34791 3.39963C3.4398 3.49313 3.49129 3.61898 3.49129 3.75008C3.49129 3.88117 3.4398 4.00702 3.34791 4.10052C3.25602 4.19402 3.13108 4.24768 3 4.24995ZM6 1.99995C5.93363 2.0011 5.86769 1.98902 5.80604 1.96442C5.74438 1.93981 5.68824 1.90317 5.6409 1.85664C5.59355 1.81011 5.55595 1.75462 5.53028 1.6934C5.50461 1.63218 5.49139 1.56646 5.49139 1.50007C5.49139 1.43369 5.50461 1.36797 5.53028 1.30675C5.55595 1.24553 5.59355 1.19004 5.6409 1.14351C5.68824 1.09698 5.74438 1.06034 5.80604 1.03574C5.86769 1.01113 5.93363 0.99905 6 1.0002C6.13108 1.00247 6.25602 1.05613 6.34791 1.14963C6.4398 1.24313 6.49129 1.36898 6.49129 1.50007C6.49129 1.63117 6.4398 1.75702 6.34791 1.85052C6.25602 1.94402 6.13108 1.99768 6 1.99995ZM10.5 7.99995C10.2236 7.99995 10.0001 7.77645 10.0001 7.5C10.0001 7.22355 10.2236 7.00005 10.5 7.00005C10.7765 7.00005 11 7.22355 11 7.5C11 7.77645 10.7765 7.99995 10.5 7.99995Z"
      fill={color}
    />
    <path
      d="M2.94975 8.3657C2.97945 8.2481 3 8.12705 3 8C3 7.17185 2.32815 6.5 1.5 6.5C0.67185 6.5 0 7.17185 0 8C0 8.82815 0.67185 9.5 1.5 9.5C1.83675 9.5 2.14425 9.3848 2.39475 9.1973L3.49995 9.9341V12.0001H4.5V9.3989L2.94975 8.3657ZM1.5 8.5001C1.43433 8.50012 1.36929 8.4872 1.30862 8.46207C1.24794 8.43694 1.19282 8.4001 1.14639 8.35366C1.09995 8.30721 1.06313 8.25208 1.03802 8.19139C1.01291 8.13071 1.00001 8.06567 1.00005 8C1.00005 7.7237 1.2237 7.50005 1.5 7.50005C1.7763 7.50005 1.99995 7.7237 1.99995 8C1.99999 8.06567 1.98709 8.13071 1.96198 8.19139C1.93687 8.25208 1.90005 8.30721 1.85362 8.35366C1.80718 8.4001 1.75206 8.43694 1.69138 8.46207C1.63071 8.4872 1.56567 8.50012 1.5 8.5001Z"
      fill={color}
    />
  </svg>
);

export const ReasonSvg = ({ color }) => (
  <svg
    className="brain-bounce"
    width={11}
    height={10}
    viewBox="0 0 11 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2.63271 9.89684L1.60112 9.12315C1.45068 9.01569 1.36471 8.86525 1.34322 8.67182C1.32173 8.45691 1.35397 8.28498 1.43993 8.15603C1.43993 8.13454 1.50441 8.05932 1.63336 7.93037L4.08339 5.60928L0.730711 5.1902C0.644745 5.16871 0.580271 5.15796 0.537288 5.15796C0.515796 5.13647 0.494305 5.12572 0.472813 5.12572C0.322373 5.08274 0.193424 4.97528 0.085966 4.80335C-2.66874e-08 4.60993 -0.0214915 4.41651 0.0214915 4.22308L0.440576 2.99807C0.483559 2.82613 0.601762 2.69718 0.795186 2.61122C0.988609 2.52525 1.16054 2.50376 1.31098 2.54674C1.33247 2.54674 1.41844 2.58973 1.56888 2.67569L4.53471 4.28756L3.9222 0.967117C3.90071 0.881151 3.88996 0.827422 3.88996 0.805931C3.88996 0.762948 3.88996 0.730712 3.88996 0.70922C3.88996 0.537288 3.95444 0.376101 4.08339 0.22566C4.23383 0.07522 4.40576 0 4.59918 0H5.88867C6.0821 0 6.24328 0.07522 6.37223 0.22566C6.52267 0.376101 6.59789 0.537288 6.59789 0.70922C6.59789 0.730712 6.59789 0.762948 6.59789 0.805931C6.59789 0.827422 6.58715 0.881151 6.56566 0.967117L5.92091 4.31979L8.91898 2.67569C9.06942 2.58973 9.15538 2.54674 9.17688 2.54674C9.34881 2.50376 9.52074 2.52525 9.69267 2.61122C9.8861 2.69718 10.0043 2.82613 10.0473 2.99807L10.4664 4.22308C10.5093 4.41651 10.4879 4.60993 10.4019 4.80335C10.3159 4.97528 10.187 5.08274 10.015 5.12572C9.99355 5.12572 9.96132 5.13647 9.91833 5.15796C9.89684 5.15796 9.84311 5.16871 9.75715 5.1902L6.37223 5.64152L8.8545 7.93037C8.98345 8.05932 9.04793 8.13454 9.04793 8.15603C9.15538 8.28498 9.19837 8.45691 9.17688 8.67182C9.15538 8.86525 9.05867 9.01569 8.88674 9.12315L7.85515 9.89684C7.68322 9.98281 7.50054 10.015 7.30711 9.99355C7.11369 9.97206 6.96325 9.8861 6.85579 9.73565C6.85579 9.71416 6.84505 9.69267 6.82355 9.67118C6.80206 9.64969 6.76983 9.59596 6.72684 9.50999L5.24393 6.41522L3.76101 9.50999C3.73952 9.59596 3.70729 9.64969 3.6643 9.67118C3.64281 9.69267 3.63207 9.71416 3.63207 9.73565C3.5461 9.8861 3.39566 9.97206 3.18074 9.99355C2.98732 10.015 2.80464 9.98281 2.63271 9.89684Z"
      fill={color}
    />
  </svg>
);

const BrainSvg = ({ color }) => (
  <svg
    className="brain-bounce"
    xmlns="http://www.w3.org/2000/svg"
    width={13}
    height={13}
    viewBox="0 0 13 13"
    fill="none"
  >
    <g clipPath="url(#clip0_1490_2100)">
      <path
        d="M12.1582 6.40625C12.1582 5.62225 11.7822 4.88625 11.1582 4.41675V4.40625C11.1576 3.95402 11.004 3.51529 10.7224 3.16143C10.4408 2.80757 10.0478 2.55939 9.6072 2.45725C9.50498 2.01674 9.25678 1.62376 8.90294 1.34217C8.54909 1.06059 8.11042 0.906957 7.6582 0.90625C7.37431 0.906166 7.09366 0.966643 6.83499 1.08365C6.57633 1.20065 6.3456 1.37149 6.1582 1.58475C5.97087 1.37141 5.74016 1.20052 5.48148 1.08351C5.2228 0.966503 4.94212 0.906069 4.6582 0.90625C4.20597 0.906867 3.76725 1.06047 3.41339 1.34207C3.05952 1.62367 2.81134 2.0167 2.7092 2.45725C2.2687 2.55947 1.87571 2.80768 1.59413 3.16152C1.31254 3.51536 1.15891 3.95404 1.1582 4.40625V4.41675C0.848843 4.64828 0.597488 4.94851 0.423953 5.29376C0.250417 5.63901 0.159444 6.01984 0.158203 6.40625C0.158203 6.84925 0.275703 7.27475 0.501203 7.65625C0.276494 8.03447 0.157996 8.46631 0.158203 8.90625C0.158203 9.74925 0.587203 10.5283 1.2917 10.9893C1.46597 11.5463 1.81399 12.0329 2.28482 12.3779C2.75564 12.7228 3.32454 12.908 3.9082 12.9063C4.34974 12.9062 4.78476 12.7998 5.17642 12.5959C5.56809 12.3921 5.90486 12.0969 6.1582 11.7353C6.41164 12.0968 6.74843 12.3919 7.14007 12.5958C7.53172 12.7996 7.96669 12.9061 8.4082 12.9063C8.99189 12.9081 9.56082 12.7229 10.0317 12.378C10.5025 12.033 10.8505 11.5463 11.0247 10.9893C11.3719 10.7636 11.6574 10.455 11.8553 10.0913C12.0532 9.72762 12.1573 9.32031 12.1582 8.90625C12.1587 8.46628 12.0401 8.03437 11.8152 7.65625C12.0399 7.27803 12.1584 6.84619 12.1582 6.40625ZM3.9082 11.9063C3.51281 11.9073 3.12886 11.7735 2.81972 11.527C2.51057 11.2805 2.2947 10.936 2.2077 10.5503C2.19164 10.4809 2.16094 10.4157 2.11765 10.3591C2.07437 10.3026 2.01949 10.2559 1.9567 10.2223C1.71692 10.0959 1.51598 9.90674 1.37534 9.67505C1.2347 9.44335 1.15965 9.17779 1.1582 8.90675C1.1582 8.56925 1.2752 8.24575 1.4977 7.97075C1.56959 7.88179 1.6088 7.77087 1.6088 7.6565C1.6088 7.54213 1.56959 7.43121 1.4977 7.34225C1.27965 7.07893 1.15967 6.74813 1.1582 6.40625C1.1582 5.87125 1.4537 5.37275 1.9297 5.10475C2.01995 5.054 2.09219 4.97646 2.13644 4.88285C2.18069 4.78925 2.19476 4.6842 2.1767 4.58225C2.16545 4.52423 2.15926 4.46534 2.1582 4.40625C2.1582 3.85475 2.6067 3.40625 3.1582 3.40625C3.29081 3.40625 3.41799 3.35357 3.51176 3.2598C3.60553 3.16604 3.6582 3.03886 3.6582 2.90625C3.6582 2.35475 4.1067 1.90625 4.6582 1.90625C5.2097 1.90625 5.6582 2.35475 5.6582 2.90625V4.90625H4.5242C4.41438 4.71523 4.24452 4.56586 4.04103 4.48135C3.83755 4.39685 3.61184 4.38195 3.39901 4.43898C3.18618 4.49601 2.99816 4.62176 2.86419 4.79669C2.73022 4.97162 2.6578 5.18591 2.6582 5.40625C2.65821 5.62638 2.73085 5.84035 2.86486 6.01498C2.99887 6.18962 3.18676 6.31516 3.39938 6.37213C3.61201 6.42911 3.83749 6.41433 4.04086 6.3301C4.24423 6.24586 4.41413 6.09688 4.5242 5.90625H5.6582V8.40625H4.5242C4.41438 8.21523 4.24452 8.06586 4.04103 7.98135C3.83755 7.89685 3.61184 7.88195 3.39901 7.93898C3.18618 7.99601 2.99816 8.12177 2.86419 8.29669C2.73022 8.47162 2.6578 8.68591 2.6582 8.90625C2.65821 9.12638 2.73085 9.34035 2.86486 9.51498C2.99887 9.68962 3.18676 9.81516 3.39938 9.87213C3.61201 9.92911 3.83749 9.91433 4.04086 9.8301C4.24423 9.74586 4.41413 9.59688 4.5242 9.40625H5.6582V10.1563C5.6582 11.1213 4.8732 11.9063 3.9082 11.9063ZM10.8187 7.97025C11.0407 8.24475 11.1582 8.56825 11.1582 8.90625C11.157 9.17734 11.082 9.44298 10.9414 9.67472C10.8007 9.90646 10.5996 10.0956 10.3597 10.2218C10.2969 10.2554 10.242 10.3021 10.1988 10.3586C10.1555 10.4152 10.1248 10.4804 10.1087 10.5498C10.0216 10.9354 9.80573 11.2799 9.4966 11.5264C9.18748 11.7729 8.80358 11.9067 8.4082 11.9058C7.4432 11.9058 6.6582 11.1208 6.6582 10.1558V7.90575H7.6582C8.4852 7.90575 9.1582 7.23275 9.1582 6.40575V6.27175C9.34922 6.16193 9.4986 5.99207 9.5831 5.78858C9.66761 5.58509 9.6825 5.35939 9.62547 5.14656C9.56845 4.93373 9.44269 4.74571 9.26776 4.61174C9.09283 4.47776 8.87854 4.40535 8.6582 4.40575C8.43808 4.40576 8.22411 4.4784 8.04947 4.61241C7.87484 4.74642 7.7493 4.9343 7.69232 5.14693C7.63535 5.35955 7.65013 5.58504 7.73436 5.78841C7.81859 5.99178 7.96758 6.16168 8.1582 6.27175V6.40575C8.1582 6.68125 7.9342 6.90575 7.6582 6.90575H6.6582V2.90625C6.6582 2.35475 7.1067 1.90625 7.6582 1.90625C8.2097 1.90625 8.6582 2.35475 8.6582 2.90625C8.6582 3.03886 8.71088 3.16604 8.80465 3.2598C8.89842 3.35357 9.0256 3.40625 9.1582 3.40625C9.7097 3.40625 10.1582 3.85475 10.1582 4.40625C10.1582 4.46675 10.1492 4.52475 10.1397 4.58225C10.1216 4.6842 10.1357 4.78925 10.18 4.88285C10.2242 4.97646 10.2965 5.054 10.3867 5.10475C10.6191 5.23386 10.813 5.42241 10.9486 5.6511C11.0841 5.8798 11.1565 6.14041 11.1582 6.40625C11.1582 6.74375 11.0412 7.06725 10.8187 7.34225C10.7468 7.43121 10.7076 7.54213 10.7076 7.6565C10.7076 7.77087 10.7468 7.88129 10.8187 7.97025Z"
        fill={color}
      />
    </g>
    <defs>
      <clipPath id="clip0_1490_2100">
        <rect
          width={12}
          height={12}
          fill="white"
          transform="translate(0.158203 0.90625)"
        />
      </clipPath>
    </defs>
  </svg>
);
